import React, { useState } from "react";
import Modal from "./Modal";
import api, { formatApiError } from "../lib/api";

// Quick product creation from the Order-New page. Stays scoped to the
// currently-selected customer; on success returns the created product so
// the caller can auto-select it on the current line without any nav.
export default function QuickAddProductModal({
    customerId,
    customerName,
    onClose,
    onCreated,
}) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");
    const [cost, setCost] = useState("");
    const [overrunPct, setOverrunPct] = useState("3");
    const [underrunPct, setUnderrunPct] = useState("3");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async () => {
        setError("");
        if (!name.trim()) {
            setError("Product name is required.");
            return;
        }
        if (!price || Number(price) < 0) {
            setError("Price is required and must be ≥ 0.");
            return;
        }
        setBusy(true);
        try {
            const { data: product } = await api.post("/products", {
                customer_id: customerId,
                name: name.trim(),
                description: description.trim(),
                price: String(price),
                cost: cost ? String(cost) : "0",
                allowed_overrun_pct: String(overrunPct || "0"),
                allowed_underrun_pct: String(underrunPct || "0"),
            });
            onCreated?.(product);
            onClose?.();
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title="Quick Add Product"
            data-testid="quick-add-product-modal"
        >
            <div className="space-y-3">
                <p className="text-xs text-gray-600">
                    Creates a new product under <b>{customerName}</b> and
                    auto-selects it on the current line. You can refine other
                    fields later from the Products page.
                </p>

                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Product name <span className="text-red-600">*</span>
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. RSC 44-C 14 X 14 X 14"
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="qap-name"
                        autoFocus
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Description
                    </label>
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Sub-title / spec details"
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="qap-description"
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Price <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            placeholder="0.0000"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono text-right"
                            data-testid="qap-price"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Cost
                        </label>
                        <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={cost}
                            onChange={(e) => setCost(e.target.value)}
                            placeholder="0.0000"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono text-right"
                            data-testid="qap-cost"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Overrun %
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={overrunPct}
                            onChange={(e) => setOverrunPct(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono text-right"
                            data-testid="qap-overrun"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Underrun %
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={underrunPct}
                            onChange={(e) => setUnderrunPct(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono text-right"
                            data-testid="qap-underrun"
                        />
                    </div>
                </div>

                {error && (
                    <div
                        className="text-xs border border-red-300 bg-red-50 text-red-800 rounded-sm px-3 py-2"
                        data-testid="qap-error"
                    >
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                        data-testid="qap-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-4 py-2 text-sm font-semibold bg-red-700 text-white rounded-sm hover:bg-red-800 disabled:bg-gray-300"
                        data-testid="qap-submit"
                    >
                        {busy ? "Creating…" : "Create & Use"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
